/**
 * Voice Announcer Component
 * Auto-plays voice announcements on all pages
 * Fetches and plays pending announcements from the backend
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Volume2, VolumeX, Bell } from 'lucide-react';
import api from '../utils/api';

const VoiceAnnouncer = () => {
  const [isEnabled, setIsEnabled] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [showIndicator, setShowIndicator] = useState(false);
  const audioRef = useRef(null);
  const lastSeenRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Check for pending announcements
  const checkForAnnouncements = useCallback(async () => {
    if (!isEnabled || isMuted) return;

    try {
      const params = lastSeenRef.current ? `?last_seen=${lastSeenRef.current}` : '';
      const response = await api.get(`/voice/pending${params}`);
      const { announcements } = response.data;

      if (announcements && announcements.length > 0) {
        // Play the most recent announcement
        const announcement = announcements[0];
        playAnnouncement(announcement);
        lastSeenRef.current = announcement.created_at;
      }
    } catch (error) {
      // Silently ignore errors - voice is non-critical
      console.debug('Voice check:', error.message);
    }
  }, [isEnabled, isMuted]);

  // Play an announcement
  const playAnnouncement = async (announcement) => {
    if (!announcement.audio_base64 || isMuted) return;

    try {
      setCurrentAnnouncement(announcement);
      setShowIndicator(true);

      // Create audio from base64
      const audioData = `data:audio/mp3;base64,${announcement.audio_base64}`;
      
      if (audioRef.current) {
        audioRef.current.src = audioData;
        audioRef.current.volume = 0.8;
        await audioRef.current.play();
      }

      // Mark as played
      try {
        await api.post(`/voice/mark-played/${announcement.id}`);
      } catch (e) {
        // Ignore mark-played errors
      }
    } catch (error) {
      console.debug('Voice play error:', error.message);
    }
  };

  // Handle audio ended
  const handleAudioEnded = () => {
    setShowIndicator(false);
    setCurrentAnnouncement(null);
  };

  // Toggle mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setShowIndicator(false);
    
    // Save preference
    localStorage.setItem('voiceAnnouncerMuted', (!isMuted).toString());
  };

  // Initialize
  useEffect(() => {
    // Load saved preference
    const savedMuted = localStorage.getItem('voiceAnnouncerMuted');
    if (savedMuted === 'true') {
      setIsMuted(true);
    }

    // Start polling for announcements
    pollIntervalRef.current = setInterval(checkForAnnouncements, 5000);
    
    // Initial check
    checkForAnnouncements();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [checkForAnnouncements]);

  // Don't render if disabled
  if (!isEnabled) return null;

  return (
    <>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        onError={handleAudioEnded}
        style={{ display: 'none' }}
      />

      {/* Voice indicator - shows when announcement is playing */}
      {showIndicator && currentAnnouncement && (
        <div 
          className="fixed bottom-20 right-4 z-50 animate-in slide-in-from-right"
          data-testid="voice-announcement-indicator"
        >
          <div className="glass-card p-3 flex items-center gap-3 max-w-xs">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center animate-pulse">
              <Volume2 className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground">Priya</p>
              <p className="text-sm truncate capitalize">
                {currentAnnouncement.event_type?.replace('_', ' ') || 'Announcement'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mute toggle button */}
      <button
        onClick={toggleMute}
        className="fixed bottom-4 right-20 z-40 w-10 h-10 rounded-full bg-card border border-border flex items-center justify-center hover:bg-muted transition-colors"
        title={isMuted ? 'Unmute announcements' : 'Mute announcements'}
        data-testid="voice-mute-toggle"
      >
        {isMuted ? (
          <VolumeX className="w-4 h-4 text-muted-foreground" />
        ) : (
          <Volume2 className="w-4 h-4 text-primary" />
        )}
      </button>
    </>
  );
};

export default VoiceAnnouncer;
